import React from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Grid,
  Paper,
} from '@mui/material';
import { ArrowBack, Assessment } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDeckDetail } from '../hooks/useDeckDetail';
import { ChatInterface } from '../components/chat/ChatInterface';
import { DeckOverview } from '../components/analysis/DeckOverview';
import { LoadingOverlay } from '../components/common/LoadingOverlay';
import { ErrorDisplay } from '../components/common/ErrorDisplay';

export const ChatPage: React.FC = () => {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate = useNavigate();
  const { deck, loading, error, refresh } = useDeckDetail(deckId!);

  if (loading) {
    return <LoadingOverlay open={true} message="Loading..." />;
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <ErrorDisplay error={error} onRetry={refresh} />
      </Container>
    );
  }

  if (!deck) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography>Deck not found</Typography>
      </Container>
    );
  }

  if (deck.status !== 'completed') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h5" gutterBottom>
          Analysis In Progress
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The chat feature will be available once the analysis is complete.
        </Typography>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate(`/analysis/${deckId}`)}
        >
          View Analysis Progress
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={3}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/decks')}
            sx={{ cursor: 'pointer' }}
          >
            My Presentations
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/analysis/${deckId}`)}
            sx={{ cursor: 'pointer' }}
          >
            {deck.fileName}
          </Link>
          <Typography color="text.primary">Chat</Typography>
        </Breadcrumbs>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/analysis/${deckId}`)}
            >
              Back to Analysis
            </Button>
            <Typography variant="h4" fontWeight={600}>
              AI Chat Assistant
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<Assessment />}
            onClick={() => navigate(`/analysis/${deckId}`)}
          >
            View Analysis
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Chat Interface - Takes priority on mobile */}
        <Grid item xs={12} lg={8} order={{ xs: 1, lg: 2 }}>
          <ChatInterface deckId={deckId!} />
        </Grid>

        {/* Deck Overview Sidebar */}
        <Grid item xs={12} lg={4} order={{ xs: 2, lg: 1 }}>
          <Paper sx={{ p: 2, position: 'sticky', top: 80 }}>
            <Typography variant="h6" gutterBottom>
              Presentation Summary
            </Typography>
            <Box sx={{ fontSize: '0.9em' }}>
              <DeckOverview deck={deck} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

